import {Form} from "../../../utils/Form";
import {areObjectsEqual, compareCallBarrings} from "../../../tools";
import {DataSource} from "./DataSource";
import {
    UpdateCallQueueMutation,
    UpdateCallQueueMutationVariables,
    useUpdateCallQueueMutation,
} from "./mutation.generated";
import {defineMessages} from "react-intl";

// type and const array for more specific type checking
export const FILE_FIELDS = ["queueFull", "queueTimeout", "waiting"] as const;
export type FileFields = (typeof FILE_FIELDS)[number];
export const FILE_FIELD_MESSAGES = defineMessages<FileFields>({
    queueFull: {id: "call_queue.media.queueFull", defaultMessage: "Voice message when queue is full"},
    queueTimeout: {id: "call_queue.media.queueTimeout", defaultMessage: "Voice message when waiting time expires"},
    waiting: {id: "call_queue.media.waiting", defaultMessage: "Wait in queue message/music"},
});

export default class CallQueueSettingsForm extends Form<UpdateCallQueueMutation, UpdateCallQueueMutationVariables> {
    protected canEditOperator: boolean;
    protected dataSource: DataSource;

    constructor(dataSource: DataSource, canEditOperator: boolean) {
        super("updateCallQueue");
        this.hooks(useUpdateCallQueueMutation, dataSource.data);
        this.dataSource = dataSource;
        this.canEditOperator = canEditOperator;
    }

    /**
     * Set a file to be deleted on submit. This has more priority than setting a new file.
     *
     * @param field The field to delete the file from.
     */
    toggleDeleted(field: FileFields) {
        this.updateValues({
            [field]: {
                unset: !this.values[field].unset,
            },
        });
    }

    setFileFieldsToNull() {
        FILE_FIELDS.forEach((field) => {
            // Can not use updateValues here because it would trigger a re-render
            this.values[field] = {
                id: undefined,
                description: undefined,
                file: undefined,
                unset: false,
            };
        });
    }

    override async submit(): Promise<boolean> {
        const res = await super.submit({
            evictCaches: [
                {
                    fieldName: "media",
                    args: {domainId: this.dataSource.domainId},
                },
            ],
        });
        if (res) {
            // Need to forget the files in the form after a successful submit
            this.setFileFieldsToNull();
            this.newInitialData(await this.dataSource.refetch());
        }
        return res;
    }

    /**
     * Returns the translation id of the error message
     */
    getFileError(field: FileFields | undefined): string | undefined {
        if (!field) return undefined;
        const error = this.errors.find((e) => e.location.includes(field));
        return error?.type;
    }

    override reset(): void {
        this.updateValues(this.initialData);
    }

    protected override get filteredVariables(): UpdateCallQueueMutationVariables {
        const values = structuredClone(this.values);
        if (!this.canEditOperator) delete values.outgoingCallBarringOperator;
        return values;
    }

    override get hasChanged(): boolean {
        const initialData = structuredClone(this.initialData);
        delete initialData.outgoingCallBarringAdmin;
        delete initialData.outgoingCallBarringOperator;

        const values = structuredClone(this.values);
        delete values.outgoingCallBarringAdmin;
        delete values.outgoingCallBarringOperator;

        return (
            !compareCallBarrings(this.initialData.outgoingCallBarringAdmin, this.values.outgoingCallBarringAdmin) ||
            !compareCallBarrings(
                this.initialData.outgoingCallBarringOperator,
                this.values.outgoingCallBarringOperator,
            ) ||
            !areObjectsEqual(initialData, values)
        );
    }
}
